/**
 * ConciergeLeads — admin table for `concierge_leads` collection.
 * Reads: GET /api/admin/concierge/leads
 * Writes: PATCH /api/admin/concierge/leads/{id}
 *
 * Renders name, email, host country, host university, submitted date, note,
 * and a status dropdown (new / contacted / closed). Sort: most recent first
 * (backend-sorted).
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mail, MapPin, GraduationCap, Clock, MessageSquare, Sparkles, Copy, X } from "lucide-react";

const STATUS_META = {
  new:       { label: "New",       cls: "bg-[color:var(--moe-yolk)]/40 text-ink border-[color:var(--moe-yolk)]" },
  contacted: { label: "Contacted", cls: "bg-[color:var(--moe-mint)] text-ink border-elec/40" },
  closed:    { label: "Closed",    cls: "bg-ink/8 text-muted border-ink/20" },
};

function _fmtDate(iso) {
  if (!iso) return "–";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric", month: "short", year: "numeric",
    });
  } catch { return iso; }
}

export default function ConciergeLeads() {
  const [leads, setLeads] = useState(null);
  const [busyId, setBusyId] = useState(null);
  // iter100 — Claude draft assistant per-row state
  const [draftingId, setDraftingId] = useState(null);
  const [draft, setDraft]           = useState(null); // { lead_id, lead_name, text, generated_at }

  const load = async () => {
    try {
      const r = await api.get("/admin/concierge/leads");
      setLeads(r.data.leads || []);
    } catch (e) {
      toast.error("Couldn't load leads");
      setLeads([]);
    }
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (leadId, newStatus) => {
    setBusyId(leadId);
    try {
      await api.patch(`/admin/concierge/leads/${leadId}`, { status: newStatus });
      setLeads((prev) => prev.map(l => l.id === leadId ? { ...l, status: newStatus } : l));
      toast.success(`Marked ${newStatus}`);
    } catch (e) {
      toast.error("Update failed");
    } finally {
      setBusyId(null);
    }
  };

  // iter100 — trigger Claude first-response draft for a lead.
  const draftResponse = async (lead) => {
    setDraftingId(lead.id);
    try {
      const r = await api.post(`/admin/concierge/leads/${lead.id}/draft-response`);
      setDraft({ lead_id: lead.id, lead_name: lead.name, lead_email: lead.email,
                 text: r.data.draft, generated_at: r.data.generated_at });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Draft generation failed.");
    } finally { setDraftingId(null); }
  };

  if (leads === null) {
    return <div className="mt-6 text-sm text-muted">Loading leads…</div>;
  }

  if (leads.length === 0) {
    return (
      <div data-testid="admin-leads-empty" className="mt-6 moe-card p-8 text-center text-muted text-sm">
        No Concierge leads yet.
      </div>
    );
  }

  const counts = leads.reduce((acc, l) => {
    const s = l.status || "new";
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  return (
    <div data-testid="admin-leads" className="mt-6">
      <div className="flex flex-wrap items-center gap-2 mb-4 text-[12px]">
        {["new", "contacted", "closed"].map((s) => (
          <span
            key={s}
            data-testid={`admin-leads-count-${s}`}
            className={`px-2.5 py-1 rounded-full border font-semibold ${STATUS_META[s].cls}`}
          >
            {STATUS_META[s].label}: {counts[s] || 0}
          </span>
        ))}
        <span className="text-muted ml-2">Total: {leads.length}</span>
      </div>

      <div className="moe-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-black/5 text-xs uppercase tracking-widest text-muted">
            <tr>
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">Email</th>
              <th className="text-left p-3">Country</th>
              <th className="text-left p-3">Host university</th>
              <th className="text-left p-3">Submitted</th>
              <th className="text-left p-3">Note</th>
              <th className="text-left p-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {leads.map((l) => (
              <tr key={l.id} data-testid={`admin-lead-row-${l.id}`} className="border-t border-ink/10 align-top">
                <td className="p-3 font-semibold text-ink">{l.name || "–"}</td>
                <td className="p-3">
                  <a
                    href={`mailto:${l.email}`}
                    className="text-elec hover:underline inline-flex items-center gap-1"
                  >
                    <Mail size={12} strokeWidth={2.4} />
                    {l.email || "–"}
                  </a>
                </td>
                <td className="p-3 text-muted">
                  <span className="inline-flex items-center gap-1">
                    <MapPin size={11} strokeWidth={2.4} />
                    {l.country || "–"}
                  </span>
                </td>
                <td className="p-3 text-muted">
                  <span className="inline-flex items-center gap-1">
                    <GraduationCap size={11} strokeWidth={2.4} />
                    {l.host_university || "–"}
                  </span>
                </td>
                <td className="p-3 text-muted whitespace-nowrap">
                  <span className="inline-flex items-center gap-1">
                    <Clock size={11} strokeWidth={2.4} />
                    {_fmtDate(l.created_at)}
                  </span>
                </td>
                <td className="p-3 text-ink/80 max-w-[280px]">
                  {l.note ? (
                    <div className="inline-flex items-start gap-1 leading-snug">
                      <MessageSquare size={11} strokeWidth={2.4} className="shrink-0 mt-0.5 text-muted" />
                      <span className="break-words">{l.note}</span>
                    </div>
                  ) : (
                    <span className="text-muted">–</span>
                  )}
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    <select
                      data-testid={`admin-lead-status-${l.id}`}
                      disabled={busyId === l.id}
                      value={l.status || "new"}
                      onChange={(e) => updateStatus(l.id, e.target.value)}
                      className={`px-2 py-1 rounded-md border text-[12px] font-semibold cursor-pointer disabled:opacity-50 ${STATUS_META[l.status || "new"].cls}`}
                    >
                      <option value="new">New</option>
                      <option value="contacted">Contacted</option>
                      <option value="closed">Closed</option>
                    </select>
                    <button
                      data-testid={`admin-lead-draft-${l.id}`}
                      disabled={draftingId === l.id}
                      onClick={() => draftResponse(l)}
                      title="Draft a first-response email with Claude"
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-elec/40 text-elec bg-elec/5 hover:bg-elec/10 text-[11px] font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Sparkles size={11} strokeWidth={2.4}/>
                      {draftingId === l.id ? "Drafting…" : "Draft reply"}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {/* iter100 — Claude-drafted first response, ready for review + copy */}
      {draft && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="admin-lead-draft-modal">
          <div className="bg-paper rounded-2xl max-w-xl w-full p-6">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-elec font-bold flex items-center gap-1">
                  <Sparkles size={11} strokeWidth={2.4}/> Draft reply
                </div>
                <div className="mt-1 text-base font-bold text-ink">
                  To {draft.lead_name} &lt;{draft.lead_email}&gt;
                </div>
                <div className="text-[11px] text-muted">
                  Draft generated by Claude Sonnet 5 · review before sending. Nothing is sent automatically.
                </div>
              </div>
              <button onClick={() => setDraft(null)} className="text-muted hover:text-ink" data-testid="admin-lead-draft-close">
                <X size={18}/>
              </button>
            </div>
            <textarea
              data-testid="admin-lead-draft-text"
              value={draft.text}
              onChange={(e) => setDraft({ ...draft, text: e.target.value })}
              rows={12}
              className="mt-4 w-full text-sm text-ink bg-white border border-ink/15 rounded-lg p-3 focus:outline-none focus:border-elec resize-y font-mono leading-relaxed"
            />
            <div className="mt-4 flex items-center justify-end gap-2">
              <button onClick={() => setDraft(null)} className="moe-btn moe-btn-ghost !py-2 text-xs">Close</button>
              <button
                data-testid="admin-lead-draft-copy"
                onClick={() => { navigator.clipboard.writeText(draft.text); toast.success("Draft copied to clipboard."); }}
                className="moe-btn moe-btn-primary !py-2 text-xs inline-flex items-center gap-1">
                <Copy size={12}/> Copy to clipboard
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
